// src/components/common/RichTextEditor.js
// Lightweight Rich Text Editor Component for React 19
import React, { useRef, useEffect, useState } from 'react';
import {
    Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter,
    AlignRight, Link, Image, Code, Heading1, Heading2, Undo, Redo
} from 'lucide-react';

const RichTextEditor = ({
    value,
    onChange,
    placeholder = 'Start typing...',
    className = '',
    showPlaceholderButton = false,
    onInsertPlaceholder
}) => {
    const editorRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (editorRef.current && value !== editorRef.current.innerHTML) {
            editorRef.current.innerHTML = value || '';
        }
    }, [value]);

    const handleInput = () => {
        if (editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    };

    const execCommand = (command, value = null) => {
        document.execCommand(command, false, value);
        editorRef.current?.focus();
        handleInput();
    };

    const insertPlaceholder = (placeholder) => {
        const placeholderSpan = `<span class="placeholder-tag" contenteditable="false" style="background-color: #e3f2fd; border: 1px solid #2196f3; border-radius: 4px; padding: 2px 8px; margin: 0 2px; color: #1976d2; font-weight: 500; cursor: pointer;">${placeholder}</span>&nbsp;`;
        document.execCommand('insertHTML', false, placeholderSpan);
        handleInput();
    };

    const ToolbarButton = ({ onClick, icon: Icon, title, active = false }) => (
        <button
            type="button"
            onClick={(e) => {
                e.preventDefault();
                onClick();
            }}
            title={title}
            className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                active ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
            }`}
        >
            <Icon className="w-4 h-4" />
        </button>
    );

    return (
        <div className={`border rounded-lg overflow-hidden ${isFocused ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800' : 'border-gray-300 dark:border-gray-600'} ${className}`}>
            {/* Toolbar */}
            <div className="flex flex-wrap gap-1 p-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-300 dark:border-gray-600">
                <div className="flex gap-1 pr-2 border-r border-gray-300 dark:border-gray-600">
                    <ToolbarButton onClick={() => execCommand('bold')} icon={Bold} title="Bold (Ctrl+B)" />
                    <ToolbarButton onClick={() => execCommand('italic')} icon={Italic} title="Italic (Ctrl+I)" />
                    <ToolbarButton onClick={() => execCommand('underline')} icon={Underline} title="Underline (Ctrl+U)" />
                </div>

                <div className="flex gap-1 pr-2 border-r border-gray-300 dark:border-gray-600">
                    <ToolbarButton onClick={() => execCommand('formatBlock', '<h1>')} icon={Heading1} title="Heading 1" />
                    <ToolbarButton onClick={() => execCommand('formatBlock', '<h2>')} icon={Heading2} title="Heading 2" />
                    <ToolbarButton onClick={() => execCommand('formatBlock', '<p>')} icon={Code} title="Paragraph" />
                </div>

                <div className="flex gap-1 pr-2 border-r border-gray-300 dark:border-gray-600">
                    <ToolbarButton onClick={() => execCommand('justifyLeft')} icon={AlignLeft} title="Align Left" />
                    <ToolbarButton onClick={() => execCommand('justifyCenter')} icon={AlignCenter} title="Align Center" />
                    <ToolbarButton onClick={() => execCommand('justifyRight')} icon={AlignRight} title="Align Right" />
                </div>

                <div className="flex gap-1 pr-2 border-r border-gray-300 dark:border-gray-600">
                    <ToolbarButton onClick={() => execCommand('insertUnorderedList')} icon={List} title="Bullet List" />
                    <ToolbarButton onClick={() => execCommand('insertOrderedList')} icon={ListOrdered} title="Numbered List" />
                </div>

                <div className="flex gap-1 pr-2 border-r border-gray-300 dark:border-gray-600">
                    <ToolbarButton
                        onClick={() => {
                            const url = prompt('Enter URL:');
                            if (url) execCommand('createLink', url);
                        }}
                        icon={Link}
                        title="Insert Link"
                    />
                </div>

                <div className="flex gap-1">
                    <ToolbarButton onClick={() => execCommand('undo')} icon={Undo} title="Undo (Ctrl+Z)" />
                    <ToolbarButton onClick={() => execCommand('redo')} icon={Redo} title="Redo (Ctrl+Y)" />
                </div>

                {showPlaceholderButton && onInsertPlaceholder && (
                    <div className="flex gap-1 ml-auto">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                onInsertPlaceholder(insertPlaceholder);
                            }}
                            className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors font-medium"
                        >
                            + Insert Placeholder
                        </button>
                    </div>
                )}
            </div>

            {/* Editor Area */}
            <div
                ref={editorRef}
                contentEditable
                onInput={handleInput}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                className="p-4 min-h-[300px] max-h-[600px] overflow-y-auto prose dark:prose-invert max-w-none focus:outline-none bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                style={{
                    wordBreak: 'break-word'
                }}
                data-placeholder={placeholder}
            />

            <style>{`
                [contenteditable]:empty:before {
                    content: attr(data-placeholder);
                    color: #9ca3af;
                    pointer-events: none;
                }
                .placeholder-tag {
                    display: inline-block;
                    user-select: none;
                }
            `}</style>
        </div>
    );
};

export default RichTextEditor;
